import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import axios from 'axios';
import toaster from '../../../../Utils/toaster';
import { useAuth } from '../../../modules/auth';
import { getRecallBotSetting, updateRecallBotSetting } from './_requests';
import './MeetingRoomsSection.css';

const API_URL = process.env.REACT_APP_API_URL;

export interface MeetingRoomDto {
  id: number;
  roomName: string;
  meetingLink: string;
  meetingId?: string | null;
  meetingPin?: string | null;
}

function isValidHttpUrl(value: string): boolean {
  try {
    const u = new URL(value.trim());
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

function isRoomIncomplete(r: MeetingRoomDto): boolean {
  const link = r.meetingLink?.trim() ?? '';
  return !link || !isValidHttpUrl(link);
}

type EditingKey = 'new' | number | null;

export function MeetingRoomsSection({ customerId }: { customerId: number }) {
  const { currentUser } = useAuth();
  const isSystemAdmin = currentUser?.result?.userType === 'SYSTEM';
  const [loading, setLoading] = useState(true);
  const [savingFeature, setSavingFeature] = useState(false);
  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [botEnabled, setBotEnabled] = useState(false);
  const [savingBot, setSavingBot] = useState(false);
  const [rejoinEnabled, setRejoinEnabled] = useState(false);
  const [savingRejoin, setSavingRejoin] = useState(false);
  const [rooms, setRooms] = useState<MeetingRoomDto[]>([]);
  const [editingKey, setEditingKey] = useState<EditingKey>(null);
  const [formRoomName, setFormRoomName] = useState('');
  const [formMeetingLink, setFormMeetingLink] = useState('');
  const [formMeetingId, setFormMeetingId] = useState('');
  const [formMeetingPin, setFormMeetingPin] = useState('');
  const [savingRoom, setSavingRoom] = useState(false);

  const load = useCallback(async () => {
    if (!customerId || customerId <= 0) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get<{
        meetingRoomsEnabled: boolean;
        rooms: MeetingRoomDto[];
      }>(`${API_URL}/Appoinment/customer-meeting-rooms/${customerId}`);
      setFeatureEnabled(res.data.meetingRoomsEnabled);
      setRooms(res.data.rooms || []);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setFeatureEnabled(false);
        setRooms([]);
      } else {
        toaster('error', err?.response?.data?.message ?? 'Failed to load meeting rooms');
      }
    } finally {
      setLoading(false);
    }

    const botSetting = await getRecallBotSetting(customerId);
    setBotEnabled(botSetting.enabled);
    setRejoinEnabled(botSetting.rejoinEnabled);
  }, [customerId]);

  useEffect(() => {
    load();
  }, [load]);

  const botSettingError = (err: any, fallback: string) =>
    err?.response?.status === 403
      ? 'Only SYSTEM administrators can change this setting'
      : err?.response?.data?.message ?? fallback;

  const toggleBot = async (enabled: boolean) => {
    if (!isSystemAdmin) return;
    setSavingBot(true);
    try {
      // Rejoin cannot outlive the bot itself; the backend enforces the same rule.
      const nextRejoin = enabled ? rejoinEnabled : false;
      await updateRecallBotSetting(customerId, { enabled, rejoinEnabled: nextRejoin });
      setBotEnabled(enabled);
      setRejoinEnabled(nextRejoin);
      toaster('success', enabled ? 'Bot integration enabled' : 'Bot integration disabled');
    } catch (err: any) {
      toaster('error', botSettingError(err, 'Failed to update bot integration'));
    } finally {
      setSavingBot(false);
    }
  };

  const toggleRejoin = async (enabled: boolean) => {
    if (!isSystemAdmin || !botEnabled) return;
    setSavingRejoin(true);
    try {
      await updateRecallBotSetting(customerId, { enabled: botEnabled, rejoinEnabled: enabled });
      setRejoinEnabled(enabled);
      toaster('success', enabled ? 'Bot rejoin enabled' : 'Bot rejoin disabled');
    } catch (err: any) {
      toaster('error', botSettingError(err, 'Failed to update bot rejoin'));
    } finally {
      setSavingRejoin(false);
    }
  };

  const toggleFeature = async (enabled: boolean) => {
    setSavingFeature(true);
    try {
      await axios.put(`${API_URL}/Appoinment/customer-meeting-rooms/${customerId}/feature`, {
        enabled,
      });
      setFeatureEnabled(enabled);
      toaster('success', enabled ? 'Meeting rooms feature enabled' : 'Meeting rooms feature disabled');
    } catch (err: any) {
      toaster('error', err?.response?.data?.message ?? 'Failed to update feature');
    } finally {
      setSavingFeature(false);
    }
  };

  const resetForm = () => {
    setFormRoomName('');
    setFormMeetingLink('');
    setFormMeetingId('');
    setFormMeetingPin('');
    setEditingKey(null);
  };

  const openAdd = () => {
    setEditingKey('new');
    setFormRoomName('');
    setFormMeetingLink('');
    setFormMeetingId('');
    setFormMeetingPin('');
  };

  const openEdit = (room: MeetingRoomDto) => {
    setEditingKey(room.id);
    setFormRoomName(room.roomName);
    setFormMeetingLink(room.meetingLink);
    setFormMeetingId(room.meetingId ?? '');
    setFormMeetingPin(room.meetingPin ?? '');
  };

  const saveRoom = async () => {
    const name = formRoomName.trim();
    const link = formMeetingLink.trim();
    if (!name || !link) {
      toaster('error', 'Room name and meeting link are required.');
      return;
    }
    if (!isValidHttpUrl(link)) {
      toaster('error', 'Meeting link must be a valid http or https URL.');
      return;
    }
    setSavingRoom(true);
    try {
      const payload = {
        roomName: name,
        meetingLink: link,
        meetingId: formMeetingId.trim() || undefined,
        meetingPin: formMeetingPin.trim() || undefined,
      };
      if (editingKey === 'new') {
        await axios.post(`${API_URL}/Appoinment/customer-meeting-rooms/${customerId}/rooms`, payload);
        toaster('success', 'Meeting room added');
      } else if (typeof editingKey === 'number') {
        await axios.put(
          `${API_URL}/Appoinment/customer-meeting-rooms/${customerId}/rooms/${editingKey}`,
          payload
        );
        toaster('success', 'Meeting room updated');
      }
      resetForm();
      await load();
    } catch (err: any) {
      toaster('error', err?.response?.data?.message ?? 'Failed to save meeting room');
    } finally {
      setSavingRoom(false);
    }
  };

  const deleteRoom = async (roomId: number) => {
    if (!window.confirm('Delete this meeting room?')) return;
    try {
      await axios.delete(`${API_URL}/Appoinment/customer-meeting-rooms/${customerId}/rooms/${roomId}`);
      toaster('success', 'Meeting room deleted');
      if (editingKey === roomId) resetForm();
      await load();
    } catch (err: any) {
      toaster('error', err?.response?.data?.message ?? 'Failed to delete');
    }
  };

  const discardNew = () => {
    if (savingRoom) return;
    resetForm();
  };

  const renderEditCard = (isNew: boolean) => (
    <div
      key={isNew ? 'edit-new' : `edit-${editingKey}`}
      className="card vis-mr-card-edit shadow-sm mb-3 overflow-hidden"
    >
      <div className="card-body p-4">
        <div className="d-flex justify-content-between align-items-start mb-4">
          <span className="fw-semibold text-gray-800">
            {isNew ? 'New meeting room' : 'Edit meeting room'}
          </span>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-sm vis-mr-btn-outline d-flex align-items-center gap-1"
              onClick={saveRoom}
              disabled={savingRoom}
            >
              <i className="bi bi-check-lg" />
              Done
            </button>
            <button
              type="button"
              className="btn btn-link text-danger p-1"
              title="Delete"
              onClick={() => {
                if (isNew) discardNew();
                else if (typeof editingKey === 'number') deleteRoom(editingKey);
              }}
              disabled={savingRoom}
            >
              <i className="bi bi-trash fs-5" />
            </button>
          </div>
        </div>

        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold fs-7">
            Meeting Room Name <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            value={formRoomName}
            onChange={(e) => setFormRoomName(e.target.value)}
            placeholder="e.g. Executive Boardroom"
            className="form-control-sm py-2"
          />
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label className="fw-semibold fs-7">
            Meeting Link <span className="text-danger">*</span>
          </Form.Label>
          <div className="input-group input-group-sm">
            <span className="input-group-text vis-mr-input-icon border-end-0">
              <i className="bi bi-link-45deg text-gray-600" />
            </span>
            <Form.Control
              value={formMeetingLink}
              onChange={(e) => setFormMeetingLink(e.target.value)}
              placeholder="https://..."
              className="border-start-0"
            />
            <span className="input-group-text vis-mr-input-icon border-start-0">
              <i className="bi bi-box-arrow-up-right text-gray-500" />
            </span>
          </div>
          <Form.Text className="text-muted small">
            The direct URL users will click to join the meeting.
          </Form.Text>
        </Form.Group>

        <div className="row g-3 mt-1">
          <div className="col-md-6">
            <Form.Label className="fw-semibold fs-7">Meeting ID (Optional)</Form.Label>
            <div className="input-group input-group-sm">
              <span className="input-group-text vis-mr-input-icon">
                <i className="bi bi-hash text-gray-600" />
              </span>
              <Form.Control
                value={formMeetingId}
                onChange={(e) => setFormMeetingId(e.target.value)}
                placeholder="Meeting ID"
              />
            </div>
          </div>
          <div className="col-md-6">
            <Form.Label className="fw-semibold fs-7">Meeting PIN (Optional)</Form.Label>
            <div className="input-group input-group-sm">
              <span className="input-group-text vis-mr-input-icon">
                <i className="bi bi-key text-gray-600" />
              </span>
              <Form.Control
                value={formMeetingPin}
                onChange={(e) => setFormMeetingPin(e.target.value)}
                placeholder="PIN"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderViewCard = (r: MeetingRoomDto) => {
    const incomplete = isRoomIncomplete(r);
    const href = r.meetingLink?.trim() && isValidHttpUrl(r.meetingLink) ? r.meetingLink : undefined;

    return (
      <div key={r.id} className="card shadow-sm mb-3 border border-gray-200">
        <div className="card-body p-4">
          <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
            <div className="flex-grow-1 min-w-0">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                <span className="fw-semibold text-gray-900">{r.roomName}</span>
                <span className="badge bg-light text-gray-800 fw-normal fs-9 px-2 py-1 border">
                  ID: {r.id}
                </span>
                {incomplete && (
                  <i className="bi bi-exclamation-circle-fill text-danger" title="Incomplete configuration" />
                )}
              </div>
              {incomplete && (
                <p className="text-muted small mb-2 mb-md-0">No link configured</p>
              )}
              {href ? (
                <a href={href} target="_blank" rel="noopener noreferrer" className="vis-mr-link small fw-medium d-inline-block">
                  {r.meetingLink}
                </a>
              ) : (
                !incomplete && (
                  <span className="small text-break text-gray-700">{r.meetingLink}</span>
                )
              )}
            </div>
            <div className="d-flex align-items-center gap-1 flex-shrink-0">
              <button
                type="button"
                className="btn btn-sm btn-light d-flex align-items-center gap-1"
                onClick={() => openEdit(r)}
              >
                <i className="bi bi-pencil" />
                Edit
              </button>
              <button
                type="button"
                className="btn btn-link text-danger p-2"
                title="Delete"
                onClick={() => deleteRoom(r.id)}
              >
                <i className="bi bi-trash fs-6" />
              </button>
            </div>
          </div>

          {!incomplete && (r.meetingId || r.meetingPin) && (
            <div className="vis-mr-detail-box p-3 mt-3">
              <div className="d-flex flex-wrap gap-4 small text-gray-800">
                {r.meetingId ? (
                  <span>
                    <i className="bi bi-hash me-1 text-gray-600" />
                    ID: {r.meetingId}
                  </span>
                ) : null}
                {r.meetingPin ? (
                  <span>
                    <i className="bi bi-key me-1 text-gray-600" />
                    PIN: {r.meetingPin}
                  </span>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-10">
        <Spinner animation="border" className="text-gray-500" />
      </div>
    );
  }

  const roomCount = rooms.length;

  return (
    <div className="vis-mr-root">
      {/* Global toggle */}
      <div className="card shadow-sm mb-5 border border-gray-200">
        <div className="card-body p-5">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-4">
            <div className="flex-grow-1 pe-md-4">
              <div className="d-flex align-items-center gap-2 mb-2">
                <h5 className="mb-0 fw-bold text-gray-900">Enable Meeting Rooms Feature</h5>
                <i
                  className="bi bi-info-circle text-danger"
                  title="When enabled, project managers can pick a saved room when creating VIS appointments."
                />
              </div>
              <p className="text-muted mb-0 small lh-base" style={{ maxWidth: 640 }}>
                When enabled, users will be able to view and select these meeting rooms when creating third-party
                (VIS) appointments. Turning this off hides meeting room selection everywhere.
              </p>
            </div>
            <div className="d-flex align-items-center gap-2 align-self-md-center">
              {savingFeature && <Spinner animation="border" size="sm" />}
              <div className="form-check form-switch m-0 ps-0">
                <Form.Check
                  type="checkbox"
                  role="switch"
                  id="meeting-rooms-feature-toggle"
                  className="vis-mr-toggle m-0"
                  checked={featureEnabled}
                  disabled={savingFeature}
                  onChange={(e) => toggleFeature(e.target.checked)}
                  style={{ width: '2.75rem', height: '1.35rem', cursor: savingFeature ? 'not-allowed' : 'pointer' }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm mb-5 border border-gray-200">
        <div className="card-body p-5">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-4">
            <div className="flex-grow-1 pe-md-4">
              <div className="d-flex align-items-center gap-2 mb-2">
                <h5 className="mb-0 fw-bold text-gray-900">Enable Bot to join meeting</h5>
              </div>
              <p className="text-muted mb-0 small lh-base" style={{ maxWidth: 640 }}>
                When enabled, a bot will join the virtual meetings and capture the interpreters check in and
                check out times.
              </p>
              {!isSystemAdmin && (
                <p className="text-muted mb-0 mt-2 small fst-italic">
                  Only SYSTEM administrators can change this setting.
                </p>
              )}
            </div>
            <div className="d-flex align-items-center gap-2 align-self-md-center">
              {savingBot && <Spinner animation="border" size="sm" />}
              <div className="form-check form-switch m-0 ps-0">
                <Form.Check
                  type="checkbox"
                  role="switch"
                  id="recall-bot-join-toggle"
                  className="vis-mr-toggle m-0"
                  checked={botEnabled}
                  disabled={savingBot || !isSystemAdmin}
                  onChange={(e) => toggleBot(e.target.checked)}
                  style={{
                    width: '2.75rem',
                    height: '1.35rem',
                    cursor: savingBot || !isSystemAdmin ? 'not-allowed' : 'pointer',
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm mb-5 border border-gray-200">
        <div className="card-body p-5">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-4">
            <div className="flex-grow-1 pe-md-4">
              <div className="d-flex align-items-center gap-2 mb-2">
                <h5 className="mb-0 fw-bold text-gray-900">Bot Rejoin Meeting</h5>
              </div>
              <p className="text-muted mb-0 small lh-base" style={{ maxWidth: 640 }}>
                When enabled, another bot will join the virtual meetings and capture the interpreters check
                in and check out times if the previous bot was expelled/kicked.
              </p>
              {!isSystemAdmin && (
                <p className="text-muted mb-0 mt-2 small fst-italic">
                  Only SYSTEM administrators can change this setting.
                </p>
              )}
              {isSystemAdmin && !botEnabled && (
                <p className="text-muted mb-0 mt-2 small fst-italic">
                  Enable "Enable Bot to join meeting" first.
                </p>
              )}
            </div>
            <div className="d-flex align-items-center gap-2 align-self-md-center">
              {savingRejoin && <Spinner animation="border" size="sm" />}
              <div className="form-check form-switch m-0 ps-0">
                <Form.Check
                  type="checkbox"
                  role="switch"
                  id="recall-bot-rejoin-toggle"
                  className="vis-mr-toggle m-0"
                  checked={rejoinEnabled}
                  disabled={savingRejoin || !isSystemAdmin || !botEnabled}
                  onChange={(e) => toggleRejoin(e.target.checked)}
                  style={{
                    width: '2.75rem',
                    height: '1.35rem',
                    cursor: savingRejoin || !isSystemAdmin || !botEnabled ? 'not-allowed' : 'pointer',
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {featureEnabled && (
        <>
          <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3 mb-4">
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <i className="bi bi-sliders text-gray-700 fs-4" />
                <h5 className="mb-0 fw-bold text-gray-900">Configured Rooms</h5>
              </div>
              <p className="text-muted small mb-0">
                {roomCount === 0
                  ? 'No rooms configured yet.'
                  : `${roomCount} room${roomCount === 1 ? '' : 's'} currently configured in the system.`}
              </p>
            </div>
            {editingKey === null && (
              <Button className="vis-mr-btn-primary px-4 py-2 fw-semibold" onClick={openAdd}>
                + Add Meeting Room
              </Button>
            )}
          </div>

          {editingKey === 'new' && renderEditCard(true)}

          {rooms.map((r) =>
            editingKey === r.id ? <React.Fragment key={r.id}>{renderEditCard(false)}</React.Fragment> : renderViewCard(r)
          )}

          {editingKey === null && roomCount === 0 && (
            <div className="text-center text-muted py-10 px-4 border border-dashed rounded bg-light">
              <p className="mb-0">No meeting rooms yet. Use &quot;+ Add Meeting Room&quot; above to create one.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
